import { useQueries, useQuery } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSession } from '../../app/session';
import { Icon } from '../../components/icons';
import { Alert, Badge, Card, EmptyState, PageHeader, buttonClasses } from '../../components/ui';
import {
  activityApi,
  analyticsApi,
  automationsApi,
  channelAccountsApi,
  contactsApi,
  inboxApi,
  tagsApi,
  workspaceApi,
  type ActivityEntry,
  type AutomationAnalyticsSummary,
  type AutomationSummary,
  type DashboardMetrics,
} from '../../lib/api';
import { formatLongDate, formatNumber, formatRelative } from '../../lib/format';
import { PlanBlockAction, useFeatures } from '../billing/plan';
import { contactDisplayName } from '../contacts/shared';
import { PRESET_KEY, PeriodBar, previousRange, useDateRange, type RangePreset } from '../analytics/range';
import { describeActivity, describeRun, groupActivity } from './activity';
import { NextUp } from './NextUp';
import { TodoQueue } from './TodoQueue';
import {
  ActivityRow,
  CardLink,
  ConversationRow,
  HealthRow,
  RowsSkeleton,
  SetupChecklist,
  StatLink,
  TokenWarning,
  errorMessage,
  expiringAccounts,
  healthOrder,
  type HealthNotes,
  percentDelta,
  type ActivityRowModel,
  type SetupStep,
} from './widgets';

/**
 * The Tableau de bord (R01, `Dashboard.dc.html`; Murmura M1,
 * `P1-Dashboard.dc.html`, D-186): "is everything working, and does anything
 * need me?" The period sits on the stat strip it controls: three stats
 * (Contacts with its new ones, Messages sent against the previous window,
 * Active automations as a current state). Then "Prochaine mise en ligne"
 * beside the "À traiter" queue, automation health by success rate beside
 * recent conversations, and activity grouped per automation run.
 * The setup checklist shows while the workspace cannot run yet.
 */

const DAY_MS = 86_400_000;
/** A live automation nothing has started for this long is flagged (critique 2026-10-29, dashboard P1 #4). */
const SILENT_DAYS = 30;
/** The latest failure shows above everything, whatever the period, for this long. */
const LATEST_FAILURE_DAYS = 7;
const FAILED = 'automation.failed';

const RECENT_CONVERSATIONS = 5;
const HEALTH_ROWS = 5;
const ACTIVITY_ROWS = 6;
/** Enough of the log for six rows once each run's entries are folded into one. */
const ACTIVITY_FETCH = 40;

function greetingKey(hour: number): 'morning' | 'afternoon' | 'evening' {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
}

const CTA = 'font-semibold text-primary-700';

// --- KPIs ------------------------------------------------------------------------------

/** "+18 %" or "=" against the same span just before (R01.1), never "0 %" in green: the badge, and the words after the label. */
function comparison(t: TFunction, { current, previous, preset }: { current: number; previous: number | undefined; preset: RangePreset }): { badge?: ReactNode; phrase: string } {
  const key = PRESET_KEY[preset];
  if (previous === undefined) return { phrase: t('dashboard.inPeriod') };
  if (current === previous) return { badge: <Badge>=</Badge>, phrase: t(`period.same.${key}`) };
  const delta = percentDelta(current, previous);
  if (!delta) return { phrase: t('dashboard.inPeriod') };
  return { badge: <Badge tone={delta.positive ? 'ok' : 'neutral'}>{delta.text}</Badge>, phrase: t(`period.versus.${key}`) };
}

/**
 * Contacts and New contacts were one fact told twice: one stat with its new
 * ones (R01.2, A01-02). Active automations is how things stand, not a figure
 * of the period: its disc is greyed. A zero is a next step, as before the
 * redesign. Murmura M1 lays the three cards out as an inline strip, with the
 * same data, comparisons and links.
 */
function Kpis({
  metrics: m,
  before,
  preset,
  automations,
}: {
  metrics: DashboardMetrics | undefined;
  before: DashboardMetrics | undefined;
  preset: RangePreset;
  automations: AutomationSummary[] | undefined;
}) {
  const { t } = useTranslation();
  const total = automations?.length;
  const drafts = automations?.filter((a) => a.status === 'draft').length ?? 0;
  const value = (n: number | undefined) => (n === undefined ? '—' : formatNumber(n));
  const sent = !m ? null : m.messagesSent === 0 && before?.messagesSent === 0 ? { phrase: t('dashboard.nothingSent') } : comparison(t, { current: m.messagesSent, previous: before?.messagesSent, preset });

  return (
    <div className="flex flex-wrap items-center gap-x-7 gap-y-3">
      {m?.totalContacts === 0 ? (
        <StatLink to="/contacts" icon="users" label={t('dashboard.contacts')} value="0" phrase={<span className={CTA}>{t('dashboard.importContacts')}</span>} />
      ) : (
        <StatLink
          to="/contacts"
          icon="users"
          label={t('dashboard.contacts')}
          value={value(m?.totalContacts)}
          badge={m && m.newContacts > 0 ? <Badge tone="ok">+{formatNumber(m.newContacts)}</Badge> : undefined}
          phrase={!m ? undefined : m.newContacts > 0 ? t('dashboard.newInPeriod', { count: m.newContacts }) : t('dashboard.noneNew')}
        />
      )}
      <StatLink to="/analytics" icon="comment" label={t('dashboard.messagesSent')} value={value(m?.messagesSent)} badge={sent?.badge} phrase={sent?.phrase} />
      {total === 0 ? (
        <StatLink
          to="/templates"
          icon="workflow"
          state
          label={t('dashboard.activeAutomations')}
          value="0"
          phrase={<span className={CTA}>{t('dashboard.startFromTemplate')}</span>}
        />
      ) : (
        <StatLink
          to="/automations"
          icon="workflow"
          state
          label={t('dashboard.activeAutomations')}
          value={value(m?.activeAutomations)}
          unit={total === undefined ? undefined : t('dashboard.ofTotal', { total })}
          phrase={drafts > 0 ? `${t('dashboard.currentState')} · ${t('dashboard.draftsLeft', { count: drafts })}` : t('dashboard.currentState')}
        />
      )}
    </div>
  );
}

// --- Verdict ---------------------------------------------------------------------------

/** Live, and nothing has started it for SILENT_DAYS: since its last run, or since it was made. */
function silentAutomations(automations: AutomationSummary[], now: number): AutomationSummary[] {
  return automations.filter((a) => a.status === 'active' && now - Date.parse(a.lastRunAt ?? a.createdAt) > SILENT_DAYS * DAY_MS);
}

/**
 * The lede says whether things work, before any figure (critique 2026-10-29,
 * dashboard P1 #1): failures in the period first, then live automations gone
 * quiet, then the period's runs. While the figures load it greets the
 * workspace, as before.
 */
function verdict(
  t: TFunction,
  { health, silent, workspace }: { health: AutomationAnalyticsSummary[] | undefined; silent: AutomationSummary[] | undefined; workspace: string },
): string {
  if (!health || !silent) return t('dashboard.lede', { workspace });
  const failing = health.filter((a) => a.executionsFailed > 0).length;
  if (failing > 0) return t('dashboard.verdict.failing', { count: failing });
  if (silent.length > 0) return t('dashboard.verdict.silent', { count: silent.length, name: silent[0]!.name });
  const runs = health.reduce((sum, a) => sum + a.executionsTotal, 0);
  return runs > 0 ? t('dashboard.verdict.allGood', { count: runs }) : t('dashboard.verdict.quiet');
}

/** The latest failed run, whatever the period: a red line above the figures for LATEST_FAILURE_DAYS. */
function LatestFailure({ automations }: { automations: AutomationSummary[] | undefined }) {
  const { t } = useTranslation();
  const latest = useQuery({ queryKey: ['activity', 'latest-failure'], queryFn: () => activityApi.list({ type: FAILED, limit: 1 }) });
  const entry = latest.data?.data.find((e) => e.type === FAILED);
  if (!entry || Date.now() - Date.parse(entry.createdAt) > LATEST_FAILURE_DAYS * DAY_MS) return null;
  const name = automations?.find((a) => a.id === entry.automationId)?.name;
  const when = formatRelative(entry.createdAt, { suffix: true, inSentence: true });
  return (
    <Alert
      tone="err"
      icon="alert"
      dense
      action={
        entry.automationId ? (
          <Link to={`/analytics/automations/${entry.automationId}`} className={`touch-target ${buttonClasses('secondary', 'sm')}`}>
            {t('dashboard.latestFailure.see')}
          </Link>
        ) : undefined
      }
    >
      {name ? t('dashboard.latestFailure.named', { when, name }) : t('dashboard.latestFailure.deleted', { when })}
    </Alert>
  );
}

/** When each failing row last failed, from the activity log: one small query per failing row, five at most. */
function useHealthNotes(rows: AutomationAnalyticsSummary[], silent: AutomationSummary[] | undefined): Map<string, HealthNotes> {
  const failing = rows.filter((a) => a.executionsFailed > 0).map((a) => a.automationId);
  const lastFailures = useQueries({
    queries: failing.map((automationId) => ({
      queryKey: ['activity', 'latest-failure', automationId],
      queryFn: () => activityApi.list({ automationId, type: FAILED, limit: 1 }),
    })),
  });
  const notes = new Map<string, HealthNotes>();
  for (const id of silent?.map((a) => a.id) ?? []) notes.set(id, { silent: true });
  failing.forEach((id, i) => {
    const entry = lastFailures[i]?.data?.data.find((e) => e.type === FAILED && e.automationId === id);
    if (entry) notes.set(id, { ...notes.get(id), lastFailureAt: entry.createdAt });
  });
  return notes;
}

// --- Activity --------------------------------------------------------------------------

/** The log folded into rows (one per run), with the names its entries point at. */
function useActivityRows(automations: AutomationSummary[] | undefined) {
  const activity = useQuery({ queryKey: ['activity', 'dashboard'], queryFn: () => activityApi.list({ limit: ACTIVITY_FETCH }) });
  const tags = useQuery({ queryKey: ['tags'], queryFn: tagsApi.list });
  const groups = useMemo(() => groupActivity(activity.data?.data ?? [], ACTIVITY_ROWS), [activity.data]);
  const contactIds = useMemo(
    () => [...new Set(groups.flatMap((g) => g.entries.flatMap((e) => (e.contactId ? [e.contactId] : []))))],
    [groups],
  );
  // At most six contacts, each cached under the key the contact page uses.
  const contacts = useQueries({
    queries: contactIds.map((id) => ({
      queryKey: ['contacts', id],
      queryFn: () => contactsApi.get(id),
      retry: false,
      staleTime: 5 * 60_000,
    })),
  });

  const rows = useMemo((): ActivityRowModel[] => {
    const automationNames = new Map((automations ?? []).map((a) => [a.id, a.name]));
    const tagNames = new Map((tags.data ?? []).map((tag) => [tag.id, tag.name]));
    const contactNames = new Map(contacts.flatMap((q) => (q.data ? [[q.data.id, contactDisplayName(q.data)] as const] : [])));
    const tagOf = (entry: ActivityEntry) => (typeof entry.metadata.tagId === 'string' ? tagNames.get(entry.metadata.tagId) : undefined);

    return groups.map(({ key, entries }) => {
      const newest = entries[0]!;
      const contactId = entries.find((e) => e.contactId)?.contactId ?? undefined;
      const automationId = entries.find((e) => e.automationId)?.automationId ?? undefined;
      const names = {
        automation: automationId ? automationNames.get(automationId) : undefined,
        contact: contactId ? contactNames.get(contactId) : undefined,
      };
      const run = key.startsWith('run:') ? describeRun(entries, names, tagOf) : null;
      return {
        id: key,
        at: newest.createdAt,
        contact: contactId && names.contact ? { id: contactId, name: names.contact } : undefined,
        line: run?.line ?? describeActivity(newest, { ...names, tag: tagOf(newest) }),
        chips: run?.chips ?? [],
      };
    });
  }, [groups, automations, tags.data, contacts]);

  return { rows, isLoading: activity.isLoading, error: activity.error };
}

// --- Page ------------------------------------------------------------------------------

export function DashboardPage() {
  const { t } = useTranslation();
  const { session } = useSession();
  const range = useDateRange('7d', { inUrl: true });
  const previous = useMemo(() => previousRange({ preset: range.preset, params: range.params }), [range.preset, range.params]);

  const workspace = useQuery({ queryKey: ['workspace', 'current'], queryFn: workspaceApi.current });
  const { maxAnalyticsDays } = useFeatures();
  // The comparison window must fit the plan's history (D-183): "30 days" on a
  // 30-day plan compares with nothing rather than earning a 402 on every visit.
  // The workspace is in the cache from the shell; while it is not, nothing is held back.
  const compareFits = maxAnalyticsDays === null || Date.parse(previous.from) >= Date.now() - maxAnalyticsDays * DAY_MS;
  const metrics = useQuery({ queryKey: ['dashboard', 'metrics', range.params], queryFn: () => analyticsApi.dashboard(range.params) });
  const before = useQuery({ queryKey: ['dashboard', 'metrics', previous], queryFn: () => analyticsApi.dashboard(previous), enabled: compareFits });
  const health = useQuery({ queryKey: ['analytics', 'automations', range.params], queryFn: () => analyticsApi.automations(range.params) });
  const automations = useQuery({ queryKey: ['automations'], queryFn: automationsApi.list });
  const channels = useQuery({ queryKey: ['channel-accounts'], queryFn: channelAccountsApi.list });
  const conversations = useQuery({
    queryKey: ['conversations', 'dashboard'],
    queryFn: () => inboxApi.list({ status: 'all', limit: RECENT_CONVERSATIONS }),
  });
  const activity = useActivityRows(automations.data);

  // "Live" = something can run: a connected channel and at least one automation.
  const connected = channels.data?.filter((c) => c.status === 'connected') ?? [];
  const setupNeeded = channels.data !== undefined && automations.data !== undefined && (connected.length === 0 || automations.data.length === 0);
  const members = useQuery({ queryKey: ['members'], queryFn: workspaceApi.members, enabled: setupNeeded });

  const now = new Date();
  const healthRows = [...(health.data ?? [])].sort(healthOrder).slice(0, HEALTH_ROWS);
  const silent = useMemo(() => (automations.data ? silentAutomations(automations.data, Date.now()) : undefined), [automations.data]);
  const healthNotes = useHealthNotes(healthRows, silent);
  const expiring = expiringAccounts(channels.data ?? []);
  const workspaceName = workspace.data?.name ?? t('dashboard.yourWorkspace');
  const greeting = t(`dashboard.greeting.${greetingKey(now.getHours())}`);
  const firstName = session?.user.firstName;

  const steps: SetupStep[] = [
    {
      title: t('dashboard.setup.connect'),
      description: connected[0] ? t('dashboard.setup.connected', { name: connected[0].displayName }) : t('dashboard.setup.connectBody'),
      icon: 'share',
      to: '/settings/channels',
      done: connected.length > 0,
    },
    {
      title: t('dashboard.setup.build'),
      description: t('dashboard.setup.buildBody'),
      icon: 'workflow',
      to: '/templates',
      done: (automations.data?.length ?? 0) > 0,
    },
    {
      title: t('dashboard.setup.import'),
      description: t('dashboard.setup.importBody'),
      icon: 'upload',
      to: '/contacts',
      done: (metrics.data?.totalContacts ?? 0) > 0,
    },
    {
      title: t('dashboard.setup.invite'),
      description: t('dashboard.setup.inviteBody'),
      icon: 'users',
      to: '/settings/people',
      done: (members.data?.length ?? 0) > 1,
    },
  ];

  return (
    <div className="@container flex flex-col gap-6">
      <LatestFailure automations={automations.data} />
      <PageHeader
        eyebrow={formatLongDate(now)}
        title={firstName ? t('dashboard.greetingNamed', { greeting, name: firstName }) : greeting}
        lede={setupNeeded ? t('dashboard.ledeSetup') : verdict(t, { health: health.data, silent, workspace: workspaceName })}
      />

      {setupNeeded ? <SetupChecklist workspaceName={workspaceName} steps={steps} /> : null}

      <section aria-label={t('dashboard.figures')} className="flex flex-col gap-3">
        <PeriodBar range={range} />
        {metrics.isError ? <Alert action={<PlanBlockAction error={metrics.error} />}>{errorMessage(metrics.error, t)}</Alert> : null}
        <Kpis metrics={metrics.data} before={before.data} preset={range.preset} automations={automations.data} />
      </section>

      {/* Automations first (D-189): their health, beside what needs someone (the open invoices are rows of the
          queue, D-186) and the latest conversations; what goes live next comes after, only when something does. */}
      <div className="flex flex-wrap items-start gap-5">
        <Card
          flush
          className="flex-[999_1_520px]"
          title={t('dashboard.health.title')}
          aside={<CardLink to="/analytics">{t('dashboard.health.seeAll')}</CardLink>}
          footer={healthRows.length > 0 ? t('dashboard.health.legend') : undefined}
        >
          {expiring.map((account) => (
            <TokenWarning key={account.id} account={account} />
          ))}
          {health.isError ? (
            <div className="px-5 py-4">
              <Alert action={<PlanBlockAction error={health.error} />}>{errorMessage(health.error, t)}</Alert>
            </div>
          ) : health.isLoading ? (
            <RowsSkeleton />
          ) : healthRows.length > 0 ? (
            <ul>
              {healthRows.map((a) => (
                <li key={a.automationId} className="border-b border-border last:border-0">
                  <HealthRow automation={a} notes={healthNotes.get(a.automationId)} />
                </li>
              ))}
            </ul>
          ) : (
            <div className="flex flex-col items-start gap-3 px-5 py-5">
              <p className="text-ds-sm text-ink-2">{t('dashboard.health.empty')}</p>
              <Link to="/templates" className={buttonClasses('secondary')}>
                <Icon name="template" className="size-[15px]" />
                {t('dashboard.health.browseTemplates')}
              </Link>
            </div>
          )}
        </Card>

        <div className="flex flex-[1_1_320px] flex-col gap-5 @min-[760px]:max-w-[420px]">
          <TodoQueue automations={automations.data} automationsLoading={automations.isLoading} />
          <Card
            flush
            title={t('dashboard.conversations.title')}
            aside={<CardLink to="/inbox">{t('dashboard.conversations.open')}</CardLink>}
          >
            {conversations.isError ? (
              <div className="px-5 py-4">
                <Alert>{errorMessage(conversations.error, t)}</Alert>
              </div>
            ) : conversations.isLoading ? (
              <RowsSkeleton />
            ) : conversations.data && conversations.data.data.length > 0 ? (
              <ul>
                {conversations.data.data.map((c) => (
                  <li key={c.id} className="border-b border-border last:border-0">
                    <ConversationRow conversation={c} />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState inline icon="inbox" title={t('dashboard.conversations.emptyTitle')} body={t('dashboard.conversations.emptyBody')} />
            )}
          </Card>
        </div>
      </div>

      <NextUp />

      <Card
        flush
        title={t('dashboard.activity.title')}
        aside={<span className="text-ds-sm text-ink-3">{t('dashboard.activity.grouped')}</span>}
      >
        {activity.error ? (
          <div className="px-5 py-4">
            <Alert>{errorMessage(activity.error, t)}</Alert>
          </div>
        ) : activity.isLoading ? (
          <RowsSkeleton />
        ) : activity.rows.length > 0 ? (
          <ul>
            {activity.rows.map((row) => (
              <ActivityRow key={row.id} row={row} />
            ))}
          </ul>
        ) : (
          <p className="px-5 py-5 text-ds-sm text-ink-3">{t('dashboard.activity.empty')}</p>
        )}
      </Card>
    </div>
  );
}
